import { useAppData, useUserStore } from "providers/RootStoreProvider";
import { ReactNode } from "react";
import { IxopayFormProps, PaymentButtonTitleVariants } from "../../types";
import { PaywallType } from "../../utils";
import UAParser from "ua-parser-js";
import { Box, Stack, SxProps, Typography } from "@mui/material";
import { PAYMENT_PLAN_TYPE } from "constants/env.const";
import { PaymentPlanType } from "enums/payment.enum";
import { useTranslation } from "react-i18next";
import { LoadingButtonProps } from "components/LoadingButton";
import { themeColors } from "constants/colors.const";
import { AppRoutes } from "enums/routes.enum";
import { Link } from "react-router-dom";
import { PaymentIconsBlock } from "../PaymentIconsBlock";
import { paymentIconsListMapPaywall } from "../../constants";
import { VATCountries } from "constants/VATCountries.const";
import { observer } from "mobx-react-lite";
import { ApplePayButton } from "components/ApplePayButton";
import { SubscriptionSubscribeClickedProperties } from "services/ampli";
import { IxopayFormTextField, PaymentMethodsDivider } from "..";
import { GooglePayButton } from "components/GooglePayButton";
import { IxopayForm } from "../../IxopayForm";

interface Props extends IxopayFormProps {
    planExtraData?: {
        oldPrice: number;
        title: string;
    };
    submitButtonProps?: Omit<LoadingButtonProps, "children"> & { title?: string };
    submitEventProps?: Omit<SubscriptionSubscribeClickedProperties, "success">;
    paywallType?: PaywallType;
    formTitle?: ReactNode;
    secureNotification?: ReactNode;
    textFieldComponent?: typeof IxopayFormTextField;
    sx?: SxProps;
}

const defaultPlanExtraData = {
    oldPrice: 9.99,
    title: "24h Trial",
};

export const FormWithDiscountAndDisclaimer = observer(
    ({ data, planExtraData = defaultPlanExtraData, secureNotification, formTitle, submitButtonProps, formatLocaleCurrency, sx }: Props) => {
        const { t } = useTranslation();
        const { amplitudeUserId, abTestData } = useAppData();
        const { userWebSession } = useUserStore();

        const deviceInfo = new UAParser().getResult();
        const deviceBrand = deviceInfo?.device?.vendor;
        const isAppleDevice = deviceBrand === "Apple";

        const isAbApplePay = abTestData?.abTestApplePay === "1";
        const isApplepay = isAbApplePay && data?.plan && isAppleDevice;
        const isAbGooglePay = abTestData?.abTestGooglePay === "1";
        const isGooglePay = isAbGooglePay && data?.plan && (!isAppleDevice || !isApplepay);
        const { oldPrice, title } = planExtraData;

        const userData = userWebSession?.session?.user;
        const { showPaymentPlans, plan } = data || {};
        const priceValue = PAYMENT_PLAN_TYPE === PaymentPlanType.SUBSCRIPTION_WITH_TRIAL ? plan?.trialPrice : plan?.price;
        const isVATUser = VATCountries.some((country) => country.code === userData?.countryCode);

        const formattedTrialPrice = formatLocaleCurrency(priceValue);
        const formattedPrice = formatLocaleCurrency(plan?.price);
        const formattedOldPrice = formatLocaleCurrency(oldPrice);

        return (
            <>
                {amplitudeUserId && data && (
                    <>
                        <Box
                            sx={{
                                "& .payment-methods-divider, & .form-title-wrap": {
                                    display: "none",
                                },
                                "&:has(.applepay-btn), &:has(.google-pay-button)": {
                                    "& .payment-methods-divider": {
                                        display: "flex",
                                    },
                                    "& .form-title-wrap": {
                                        display: "block",
                                    },
                                },
                                "& #gpay-button-online-api-id": { outline: "1px solid #efefef !important" },
                                ...sx,
                            }}
                        >
                            <Typography
                                className="plan-name"
                                variant="title3Bold"
                                fontSize="20px"
                                textAlign="center"
                                sx={(theme) => ({
                                    display: "block",
                                    m: { xs: "0 0 .25rem", sm: "3.5rem 0 .25rem" },
                                    color: theme.palette.text.secondary,
                                })}
                            >
                                {t(title)}
                            </Typography>
                            <Stack direction="row" gap=".5rem" justifyContent="center" className="price">
                                <Box sx={{ position: "relative" }}>
                                    <Typography variant="body1CaptionBold" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                                        {formattedOldPrice}
                                    </Typography>
                                    <Box
                                        sx={(theme) => ({
                                            position: "absolute",
                                            width: "85%",
                                            height: "2px",
                                            background: theme.palette.background.brightGreen,
                                            top: "50%",
                                            left: "8%",
                                            transform: "rotate(-40deg)",
                                        })}
                                    />
                                </Box>
                                <Typography variant="body1CaptionBold" fontSize="18px" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                                    {formattedTrialPrice}
                                </Typography>
                            </Stack>
                            {(isApplepay || isAbGooglePay) && (
                                <Box sx={{ mt: "16px" }} className="alternative-providers-wrap">
                                    <Box className="form-title-wrap">{formTitle}</Box>
                                    {isApplepay && <ApplePayButton data={data} />}
                                    {isGooglePay && <GooglePayButton data={data} />}
                                    <PaymentMethodsDivider sx={{ mt: "12px" }} title={t("or pay by a card")} />
                                </Box>
                            )}
                            <Typography
                                align="center"
                                sx={(theme) => ({
                                    display: "block",
                                    mb: "0.75rem",
                                    px: "1rem",
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary,
                                })}
                            >
                                {t("renewal_summary", { trialPrice: formattedTrialPrice, price: formattedPrice })}
                            </Typography>
                            <Stack
                                padding="0 1rem"
                                direction="column"
                                flex={1}
                                className="form-inputs-wrap"
                                sx={{
                                    mb: "1.5rem",
                                    spacing: {
                                        xs: 0,
                                        sm: "15px",
                                    },
                                }}
                            >
                                <Box className="box--empty" />
                                {showPaymentPlans && (
                                    <Typography variant="h3" alignSelf="center" align="center" maxWidth="260px">
                                        {plan?.name}
                                    </Typography>
                                )}
                                <IxopayForm
                                    data={data}
                                    paywall={PaywallType.map}
                                    customLabels={{
                                        submit_title: submitButtonProps?.title ?? t(PaymentButtonTitleVariants.START_TRIAL),
                                    }}
                                    sx={{
                                        "& .ixopay-submit-button": {
                                            width: "calc(100% + 32px)",
                                            ml: "-16px",
                                            height: "44px",
                                            textTransform: "uppercase",
                                            backgroundColor: "background.brightGreen",
                                            border: "none",
                                            color: "text.secondary",
                                            ...(submitButtonProps?.sx as any),
                                        },
                                    }}
                                />
                            </Stack>
                            {secureNotification}
                            <Box
                                sx={(theme) => ({
                                    padding: { xs: 0, sm: "0 12px" },
                                    background: { xs: "none", sm: theme.palette.background.lightGrey },
                                    borderRadius: "24px",
                                    width: "100%",
                                })}
                            >
                                <Stack direction="column" maxWidth="380px" alignItems="center" sx={{ textAlign: "left", padding: { xs: "0 1rem", sm: 0 } }}>
                                    <Typography variant="smallInterRegular" color={themeColors.text.lighterGrey} sx={{ mb: "1rem", lineHeight: "20px" }}>
                                        {submitButtonProps?.title
                                            ? t("click_button_disclaimer", {
                                                  title:
                                                      (submitButtonProps?.title as string).charAt(0).toUpperCase() +
                                                      (submitButtonProps?.title as string).slice(1),
                                              })
                                            : t("By clicking “Continue”, you accept our")}{" "}
                                        <Link to={AppRoutes.TERMS_OF_USE} style={{ color: themeColors.background.brightGreen }}>
                                            {t("Terms of Use_disclaimer")}
                                        </Link>{" "}
                                        {t("and")}{" "}
                                        <Link to={AppRoutes.PRIVACY_POLICY} style={{ color: themeColors.background.brightGreen }}>
                                            {t("Privacy Notice")}
                                        </Link>
                                    </Typography>
                                    <PaymentIconsBlock iconsList={paymentIconsListMapPaywall} />
                                    <Typography
                                        variant="smallThin"
                                        className="disclaimer"
                                        sx={{
                                            lineHeight: "18px",
                                            fontSize: { xs: "13px", sm: "14px" },
                                            color: themeColors.text.lighterGrey,
                                            mt: "24px",
                                        }}
                                    >
                                        {t("Payment_form_descr_p1")}{" "}
                                        <span style={{ fontWeight: 500 }}>
                                            {t("Payment_form_descr_p2", {
                                                price: formattedPrice,
                                            })}
                                        </span>{" "}
                                        {
                                            // TODO replace hardcode if subscription duration changes
                                            t("every month")
                                        }{" "}
                                        {t("Payment_form_descr_p4")}{" "}
                                        <Link
                                            to={{ pathname: "/terms-of-use", hash: "subscription-terms" }}
                                            style={{
                                                textDecoration: "underline",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {t("Subscription Terms")}.
                                        </Link>{" "}
                                        {isVATUser && t("Payment_form_descr_p6")}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    </>
                )}
            </>
        );
    }
);
